import { motion } from 'framer-motion';
import { Play, Clock, User, Target } from 'lucide-react';
import type { Video, Difficulty, MuscleGroup } from '../types';

interface VideoCardProps {
  video: Video;
  onSelect: (video: Video) => void;
  onCategoryClick?: (category: string) => void;
  onMuscleGroupClick?: (muscleGroup: MuscleGroup) => void;
}

const difficultyColors: Record<Difficulty, string> = {
  beginner: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  intermediate: 'bg-amber-100 text-amber-700 border-amber-200',
  advanced: 'bg-rose-100 text-rose-700 border-rose-200'
};

const difficultyLabels: Record<Difficulty, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced'
};

const muscleGroupColors: Record<MuscleGroup, string> = {
  'core': 'bg-rose-50 text-rose-600 border-rose-200',
  'upper-body': 'bg-blue-50 text-blue-600 border-blue-200',
  'lower-body': 'bg-purple-50 text-purple-600 border-purple-200',
  'full-body': 'bg-green-50 text-green-600 border-green-200',
  'flexibility': 'bg-teal-50 text-teal-600 border-teal-200',
  'back': 'bg-indigo-50 text-indigo-600 border-indigo-200',
  'glutes': 'bg-orange-50 text-orange-600 border-orange-200'
};

const muscleGroupLabels: Record<MuscleGroup, string> = {
  'core': 'Core',
  'upper-body': 'Upper Body',
  'lower-body': 'Lower Body',
  'full-body': 'Full Body',
  'flexibility': 'Flexibility',
  'back': 'Back',
  'glutes': 'Glutes'
};

export default function VideoCard({ video, onSelect, onCategoryClick, onMuscleGroupClick }: VideoCardProps) {
  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(video)}
      className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Play Button */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
          <motion.div
            initial={{ scale: 0.8 }}
            whileHover={{ scale: 1.1 }}
            className="w-16 h-16 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-2xl"
          >
            <Play className="w-7 h-7 text-rose-600 ml-1" />
          </motion.div>
        </div>

        {/* Duration Badge */}
        <div className="absolute bottom-3 right-3 px-3 py-1 bg-black/70 backdrop-blur-sm rounded-full flex items-center gap-1.5 text-white text-sm">
          <Clock className="w-3.5 h-3.5" />
          <span>{video.duration} min</span>
        </div>

        {/* Difficulty Badge */}
        <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold border ${difficultyColors[video.difficulty]}`}>
          {difficultyLabels[video.difficulty]}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-bold text-gray-900 text-lg mb-2 line-clamp-2 group-hover:text-rose-600 transition-colors">
          {video.title}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-2 mb-3">
          {video.description}
        </p>
        
        {/* Category & Muscle Group Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {onCategoryClick ? (
            <button 
              onClick={(e) => { e.stopPropagation(); onCategoryClick(video.category); }}
              className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-xs text-gray-600 transition-colors"
            >
              {video.category}
            </button>
          ) : (
            <div className="px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
              {video.category}
            </div>
          )}
          
          {onMuscleGroupClick && (
            <button 
              onClick={(e) => { e.stopPropagation(); onMuscleGroupClick(video.muscleGroup); }}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${muscleGroupColors[video.muscleGroup] || 'bg-gray-100 text-gray-600 border-gray-200'}`}
            >
              {muscleGroupLabels[video.muscleGroup] || video.muscleGroup}
            </button>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <User className="w-4 h-4" />
            <span>{video.instructor}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
